import { inject, Injectable, InjectionToken, OnDestroy } from "@angular/core";
import { ActivatedRoute, ParamMap } from "@angular/router";
import { Store } from "@ngxs/store";
import { OrganizationState } from "@vp/data-access/organization";
import { ContentData, Organization, User } from "@vp/models";
import { filterNullMap } from "@vp/shared/operators";
import { AppStoreService } from "@vp/shared/store/app";
import { nanoid } from "nanoid";
import { createPatch } from "rfc6902";
import { combineLatest, Observable, of, Subject, zip } from "rxjs";
import { map, switchMap, take, takeUntil, tap, withLatestFrom } from "rxjs/operators";
import { ContentApiService } from "../api/content-api-service";
import { ContentDataFilter } from "../models/content-data-filter";
import { ContentOperations } from "../models/content-operations.model";
import * as ContentFilterStateActions from "../state+/content-filter-state.actions";
import { ContentFilterState } from "../state+/content-filter.state";

export const CONTENT_MANAGEMENT_API_BASE_URL = new InjectionToken<string>("API_BASE_URL");

export const DEFAULT_PAGER_LIST = [5, 10, 25, 100];

export interface ContentDetails {
  contentId: string;
  contentTypeId: string;
}

@Injectable({
  providedIn: "root"
})
export class ContentManagementService implements OnDestroy {
  organization$: Observable<Organization | null> = inject(Store).select(
    OrganizationState.organization
  );
  workingCopy$: Observable<ContentData | null> = inject(Store).select(
    ContentFilterState.workingCopy
  );
  contentData$: Observable<ContentData | null> = inject(Store).select(
    ContentFilterState.contentData
  );
  contentDataFilter$: Observable<Partial<ContentDataFilter>> = inject(Store).select(
    ContentFilterState.currentFilter
  );
  private readonly _destroyed$ = new Subject<void>();

  contentTypes$ = this.organization$.pipe(
    filterNullMap(),
    map(org => {
      return org.contentTypes ?? [];
    }),
    takeUntil(this._destroyed$)
  );

  queryParams$ = this.activatedRoute.queryParamMap.pipe(
    takeUntil(this._destroyed$),
    filterNullMap(),
    map(this.getStateFromParams)
  );

  contentDetails$ = this.queryParams$.pipe(
    map(paramMap => {
      return {
        contentId: paramMap.contentId,
        contentTypeId: paramMap.contentTypeId
      } as ContentDetails;
    })
  );

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly store: Store,
    private readonly contentApiService: ContentApiService,
    private readonly appStoreService: AppStoreService
  ) {
    this.queryParams$
      .pipe(
        tap(paramMap => {
          this.store.dispatch(
            new ContentFilterStateActions.SetFilter({
              contentTypeId: paramMap.contentTypeId,
              contentId: paramMap.contentId,
              search: paramMap.search,
              skip: paramMap.skip,
              take: paramMap.take
            })
          );
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  loadExistingContent(partialContentData: Partial<ContentData>): Observable<ContentData> {
    if (partialContentData.contentId) {
      return this.getContent(partialContentData.contentId).pipe(
        tap((workingCopy: ContentData) => {
          if (workingCopy) {
            this.store.dispatch(new ContentFilterStateActions.SetContentData(workingCopy));
            this.store.dispatch(new ContentFilterStateActions.SetWorkingCopy(workingCopy));
          }
        })
      );
    } else {
      this.store.dispatch(new ContentFilterStateActions.LoadContentData(partialContentData, true));
      return of(partialContentData as ContentData);
    }
  }

  updateWorkingCopy = () => {
    return combineLatest([
      this.contentData$.pipe(filterNullMap()),
      this.workingCopy$.pipe(filterNullMap())
    ]).pipe(
      take(1),
      map(([original, merged]) => {
        return this.getOperations(original, merged);
      }),
      tap(contentOperations => {
        this.store.dispatch(new ContentFilterStateActions.SetPendingOperations(contentOperations));
      })
    );
  };

  updateTags = (contentData: ContentData) => {
    return this.appStoreService.user$
      .pipe(
        take(1),
        withLatestFrom(this.contentData$.pipe(filterNullMap())),
        switchMap(([user, original]: [User, ContentData]) => {
          const updated = {
            ...contentData,
            lastUpdatedBy: user.userId,
            tags: contentData.tags
          };
          return zip(
            of(original),
            of(updated),
            this.store.dispatch(new ContentFilterStateActions.SetWorkingCopy(updated))
          );
        }),
        map(([original, updated]) => {
          return this.getOperations(original, updated);
        }),
        tap(contentOperations => {
          this.store.dispatch(
            new ContentFilterStateActions.SetPendingOperations(contentOperations)
          );
        })
      )
      .subscribe();
  };

  getContent = (contentId: string) => {
    return this.contentApiService.getContent(contentId);
  };

  downloadContent = (contentId: string) => {
    return this.contentApiService.downloadContent(contentId);
  };

  private getOperations(original: ContentData, updated: ContentData) {
    return {
      contentId: updated.contentId,
      operations: createPatch(original, updated)
    } as ContentOperations;
  }

  createOrEditContent(content: ContentData) {
    if (content.contentId) {
      return this.store.dispatch(new ContentFilterStateActions.CommitOperations());
    } else {
      const newContent = { ...content, contentId: nanoid() };
      return this.store.dispatch(new ContentFilterStateActions.AddContent(newContent));
    }
  }

  getContentsByCaseId(caseId: string): Observable<ContentData[]> {
    return this.contentApiService.getContentsByCaseId(caseId);
  }

  getDefaultContent(contentId: string, contentTypeId: string) {
    return {
      contentId: contentId,
      active: true,
      roles: [],
      permissionTags: [],
      content: "",
      contentTypeId: contentTypeId === "all" ? null : contentTypeId,
      isPublic: true,
      tags: []
    } as Partial<ContentData>;
  }

  private getStateFromParams(paramMap: ParamMap): PageParams {
    const search = paramMap.get("search") || null;
    const take = Number(paramMap.get("take"));
    const skip = Number(paramMap.get("skip"));
    const contentId = paramMap.get("contentId");
    const contentTypeId = paramMap.get("contentTypeId") || "all";
    return {
      pageSizeOptions: DEFAULT_PAGER_LIST,
      search: search,
      take: take,
      skip: skip,
      pageIndex: skip / take,
      contentTypeId: contentTypeId,
      contentId: contentId
    } as PageParams;
  }
}

export interface PageParams {
  pageSizeOptions: number[];
  search: string;
  take: number;
  skip: number;
  pageIndex: number;
  deptId: string;
  contentTypeId: string;
  contentId: string;
}
